import { Head, Link, useForm, usePage } from '@inertiajs/react'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import ImageUpload from '@/components/ImageUpload'
import type { SharedProps } from '@/types'

type Props = {
  service_types: string[]
  price_ranges: string[]
  additional_service_options: string[]
}

export default function EventServiceSubmit({ service_types, price_ranges, additional_service_options }: Props) {
  const { app_url } = usePage<SharedProps>().props

  const { data, setData, post, processing, errors } = useForm({
    event_service: {
      name: '',
      service_type: '',
      description: '',
      price_range: '',
      contact_first_name: '',
      contact_last_name: '',
      contact_email: '',
      phone: '',
      website: '',
      street_address: '',
      city: '',
      state: '',
      zip: '',
      hide_address: false,
      instagram_url: '',
      twitter_url: '',
      facebook_url: '',
      linkedin_url: '',
      youtube_url: '',
      tiktok_url: '',
      years_experience: '' as string | number,
      portfolio_url: '',
      video_url: '',
      additional_services: [] as string[],
      capacity: '' as string | number,
      seated_capacity: '' as string | number,
      standing_capacity: '' as string | number,
      square_footage: '' as string | number,
      floors: '' as string | number,
      floor_plan_url: '',
      private_rooms: '',
    },
    cover_image: '' as string,
    logo: '' as string,
    nickname: '',
  })

  function updateField(field: string, value: string | boolean | number | string[]) {
    setData('event_service', { ...data.event_service, [field]: value })
  }

  function toggleAdditionalService(svc: string) {
    const current = data.event_service.additional_services
    const updated = current.includes(svc) ? current.filter(s => s !== svc) : [...current, svc]
    updateField('additional_services', updated)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post('/services/submit')
  }

  const isVenue = data.event_service.service_type === 'Venue'
  const fieldError = (key: string) => errors[`event_service.${key}` as keyof typeof errors]

  return (
    <>
      <Head title="Submit your business — Gala Season Directory">
        <meta head-key="description" name="description" content="List your event services business — caterer, photographer, AV, florist, venue, and more — in the Gala Season directory. Free, reviewed by our team before going live." />
        <meta head-key="og:title" property="og:title" content="Submit your business — Gala Season Directory" />
        <meta head-key="og:description" property="og:description" content="List your event services business in the Gala Season directory. Free, reviewed by our team before going live." />
        <meta head-key="og:type" property="og:type" content="website" />
        <meta head-key="og:url" property="og:url" content={`${app_url}/services/submit`} />
        <meta head-key="twitter:card" name="twitter:card" content="summary" />
        <meta head-key="twitter:title" name="twitter:title" content="Submit your business — Gala Season Directory" />
      </Head>

      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
        <Link
          href="/services"
          className="mb-4 inline-flex items-center gap-1 text-base text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-3.5" />
          Back to Directory
        </Link>

        <h1 className="text-4xl font-semibold tracking-tight text-balance">List your business in the Gala Season directory</h1>
        <p className="mt-3 text-lg text-foreground/80">
          Nonprofit event organizers use this directory to find vendors for their galas. Submissions are reviewed by our team before they go live — usually within a few business days. Free, no account required.
        </p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          {/* Honeypot — hidden from real users, attractive to bots */}
          <div aria-hidden="true" className="absolute size-px overflow-hidden -left-[9999px] top-auto">
            <label htmlFor="nickname">Leave this blank</label>
            <input
              id="nickname"
              type="text"
              name="nickname"
              tabIndex={-1}
              autoComplete="off"
              value={data.nickname}
              onChange={e => setData('nickname', e.target.value)}
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Business basics</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">
                  Business name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="name"
                  value={data.event_service.name}
                  onChange={e => updateField('name', e.target.value)}
                />
                {fieldError('name') && <p className="text-base text-destructive">{fieldError('name')}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="service_type">
                  Category <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={data.event_service.service_type}
                  onValueChange={v => updateField('service_type', v)}
                >
                  <SelectTrigger id="service_type">
                    <SelectValue placeholder="Pick what you do" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {[...service_types].sort().map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                {fieldError('service_type') && <p className="text-base text-destructive">{fieldError('service_type')}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  rows={4}
                  placeholder="Tell event organizers what makes your business a good fit."
                  value={data.event_service.description}
                  onChange={e => updateField('description', e.target.value)}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="price_range">Price range</Label>
                  <Select
                    value={data.event_service.price_range}
                    onValueChange={v => updateField('price_range', v)}
                  >
                    <SelectTrigger id="price_range">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        {price_ranges.map(range => (
                          <SelectItem key={range} value={range}>{range}</SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="years_experience">Years of experience</Label>
                  <Input
                    id="years_experience"
                    type="number"
                    min={0}
                    value={data.event_service.years_experience}
                    onChange={e => updateField('years_experience', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>How nonprofits will reach you</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact_first_name">First name</Label>
                  <Input
                    id="contact_first_name"
                    value={data.event_service.contact_first_name}
                    onChange={e => updateField('contact_first_name', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_last_name">Last name</Label>
                  <Input
                    id="contact_last_name"
                    value={data.event_service.contact_last_name}
                    onChange={e => updateField('contact_last_name', e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact_email">
                  Public contact email <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={data.event_service.contact_email}
                  onChange={e => updateField('contact_email', e.target.value)}
                />
                <p className="text-sm text-muted-foreground">
                  Your email and phone number will appear publicly on your listing so event organizers can reach you. We'll also use this email to send you submission updates.
                </p>
                {fieldError('contact_email') && <p className="text-base text-destructive">{fieldError('contact_email')}</p>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={data.event_service.phone}
                    onChange={e => updateField('phone', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    placeholder="https://"
                    value={data.event_service.website}
                    onChange={e => updateField('website', e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="portfolio_url">Portfolio URL</Label>
                <Input
                  id="portfolio_url"
                  placeholder="https://"
                  value={data.event_service.portfolio_url}
                  onChange={e => updateField('portfolio_url', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Service area</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="street_address">Street address</Label>
                <Input
                  id="street_address"
                  value={data.event_service.street_address}
                  onChange={e => updateField('street_address', e.target.value)}
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={data.event_service.city}
                    onChange={e => updateField('city', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    value={data.event_service.state}
                    onChange={e => updateField('state', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="zip">Zip</Label>
                  <Input
                    id="zip"
                    value={data.event_service.zip}
                    onChange={e => updateField('zip', e.target.value)}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="hide_address"
                  checked={data.event_service.hide_address}
                  onCheckedChange={v => updateField('hide_address', v)}
                />
                <Label htmlFor="hide_address">Hide street address from public profile (still used for service area)</Label>
              </div>
            </CardContent>
          </Card>

          {isVenue && (
            <Card>
              <CardHeader>
                <CardTitle>Venue details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="capacity">Total capacity</Label>
                    <Input
                      id="capacity"
                      type="number"
                      min={0}
                      value={data.event_service.capacity}
                      onChange={e => updateField('capacity', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="seated_capacity">Seated</Label>
                    <Input
                      id="seated_capacity"
                      type="number"
                      min={0}
                      value={data.event_service.seated_capacity}
                      onChange={e => updateField('seated_capacity', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="standing_capacity">Standing</Label>
                    <Input
                      id="standing_capacity"
                      type="number"
                      min={0}
                      value={data.event_service.standing_capacity}
                      onChange={e => updateField('standing_capacity', e.target.value)}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="square_footage">Square footage</Label>
                    <Input
                      id="square_footage"
                      type="number"
                      min={0}
                      value={data.event_service.square_footage}
                      onChange={e => updateField('square_footage', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="floors">Floors</Label>
                    <Input
                      id="floors"
                      type="number"
                      min={0}
                      value={data.event_service.floors}
                      onChange={e => updateField('floors', e.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="private_rooms">Private rooms / waiting areas</Label>
                  <Input
                    id="private_rooms"
                    placeholder="e.g. 2 VIP green rooms"
                    value={data.event_service.private_rooms}
                    onChange={e => updateField('private_rooms', e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {additional_service_options.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Additional services offered</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {additional_service_options.map(svc => (
                    <label key={svc} className="flex items-center gap-2 text-base">
                      <Checkbox
                        checked={data.event_service.additional_services.includes(svc)}
                        onCheckedChange={() => toggleAdditionalService(svc)}
                      />
                      {svc}
                    </label>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Media</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Cover image</Label>
                <ImageUpload
                  onUpload={signedId => setData('cover_image', signedId || '')}
                />
              </div>
              <div className="space-y-2">
                <Label>Business logo</Label>
                <ImageUpload
                  onUpload={signedId => setData('logo', signedId || '')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="video_url">Video URL</Label>
                <Input
                  id="video_url"
                  placeholder="https://youtube.com/..."
                  value={data.event_service.video_url}
                  onChange={e => updateField('video_url', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Social media (optional)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                { key: 'instagram_url', label: 'Instagram' },
                { key: 'twitter_url', label: 'X / Twitter' },
                { key: 'facebook_url', label: 'Facebook' },
                { key: 'linkedin_url', label: 'LinkedIn' },
                { key: 'youtube_url', label: 'YouTube' },
                { key: 'tiktok_url', label: 'TikTok' },
              ].map(({ key, label }) => (
                <div key={key} className="space-y-2">
                  <Label htmlFor={key}>{label}</Label>
                  <Input
                    id={key}
                    placeholder="https://"
                    value={(data.event_service as Record<string, string | boolean | number | string[]>)[key] as string}
                    onChange={e => updateField(key, e.target.value)}
                  />
                </div>
              ))}
            </CardContent>
          </Card>

          <div className="flex items-center justify-between gap-3 pt-2">
            <p className="text-sm text-muted-foreground">
              By submitting, you confirm you have the authority to list this business.
            </p>
            <Button type="submit" disabled={processing} size="lg">
              {processing ? 'Submitting…' : 'Submit for review'}
            </Button>
          </div>
        </form>
      </div>
    </>
  )
}
